import React, { useState, useRef, useEffect } from 'react';
import OpenAI from 'openai';
import { marked } from 'marked';
import './Chat.css';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatProps {
  alwaysOpen?: boolean;
}

const Chat: React.FC<ChatProps> = ({ alwaysOpen = false }) => {
  const [isOpen, setIsOpen] = useState(alwaysOpen);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      
      if (!apiKey) {
        throw new Error('API key not found. Please check your .env file.');
      }

      const openai = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true
      });

      const assistantMessage: Message = {
        id: generateId(),
        role: 'assistant',
        content: '',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Debug the request payload
      const requestPayload = {
        model: "gpt-4.1",
        input: [
          {
            role: "system",
            content: [
              {
                type: "input_text",
                text: "Bạn là trợ lí của tuyển sinh của hệ thống trường Einstein. Hãy trả lời các câu hỏi của phụ huynh dựa trên nội dung tìm kiếm. Hãy trả lời cụ thể và đầy đủ. QUAN TRỌNG: Luôn định dạng câu trả lời bằng Markdown với các tiêu đề, danh sách có dấu đầu dòng, văn bản in đậm cho số tiền và thông tin quan trọng, để câu trả lời dễ đọc và có cấu trúc rõ ràng."
              }
            ]
          } as any,
          ...messages.map(msg => ({
            role: msg.role === 'user' ? 'user' : 'assistant',
            content: [{ 
              type: msg.role === 'user' ? "input_text" : "output_text", 
              text: msg.content 
            }]
          } as any)),
          {
            role: "user",
            content: [{ type: "input_text", text: userMessage.content }]
          } as any
        ],
        text: {
          format: {
            type: "text"
          }
        },
        reasoning: {},
        tools: [
          {
            type: "file_search",
            vector_store_ids: [
              "vs_68a2516788d881919f3e05da53d1eec7"
            ]
          }
        ],
        temperature: 0.25,
        max_output_tokens: 4096,
        top_p: 1,
        store: false
      };
      
      const response = await openai.responses.create(requestPayload as any);

      // Extract the response text - try multiple paths
      let responseText = 'Xin lỗi, không nhận được phản hồi.';
      
      // Method 1: Check if there's a direct output_text field
      if ((response as any).output_text) {
        responseText = (response as any).output_text;
      } 
      // Method 2: Look for message type in output array
      else {
        const messageOutput = (response as any).output?.find((item: any) => item.type === 'message');
        if (messageOutput?.content?.[0]?.text) {
          responseText = messageOutput.content[0].text;
        }
      }
      
      setMessages(prev => 
        prev.map(msg => 
          msg.id === assistantMessage.id 
            ? { ...msg, content: responseText }
            : msg
        )
      );

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: generateId(),
        role: 'assistant',
        content: 'Xin lỗi, có lỗi xảy ra. Vui lòng thử lại sau.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const startNewConversation = () => {
    setMessages([]);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Bubble - only show if not alwaysOpen */}
      {!alwaysOpen && (
        <div 
          className={`chat-bubble ${isOpen ? 'hidden' : ''}`}
          onClick={() => setIsOpen(true)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z" fill="white"/>
            <circle cx="8" cy="10" r="1" fill="white"/>
            <circle cx="12" cy="10" r="1" fill="white"/>
            <circle cx="16" cy="10" r="1" fill="white"/>
          </svg>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-title">
              <h3>Hỗ trợ tuyển sinh Einstein</h3>
              <p>Chúng tôi sẵn sàng giúp đỡ bạn!</p>
            </div>
            <div className="chat-actions">
              <button 
                className="new-chat-btn"
                onClick={startNewConversation}
                title="Cuộc trò chuyện mới"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M19 7V4C19 3.45 18.55 3 18 3H5C4.45 3 4 3.45 4 4V18L7 15H18C18.55 15 19 14.55 19 14V13" stroke="currentColor" strokeWidth="2"/>
                  <path d="M21 6V9" stroke="currentColor" strokeWidth="2"/>
                  <path d="M19.5 7.5H22.5" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </button>
              {!alwaysOpen && (
                <button 
                  className="close-btn"
                  onClick={() => setIsOpen(false)}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="chat-messages">
            {messages.length === 0 && (
              <div className="welcome-message">
                <p>Xin chào! Tôi là trợ lý tuyển sinh của trường Einstein. Tôi có thể giúp bạn những thông tin gì về trường chúng tôi?</p>
              </div>
            )}
            
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`message ${message.role}`}
              >
                <div 
                  className="message-content"
                  dangerouslySetInnerHTML={
                    message.role === 'assistant' 
                      ? { __html: marked.parse(message.content) as string }
                      : undefined
                  }
                >
                  {message.role === 'user' ? message.content : null}
                </div>
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="message assistant">
                <div className="message-content typing">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Nhập câu hỏi của bạn..."
              disabled={isLoading}
              rows={1}
            />
            <button 
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="send-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chat;